import { mfiApi } from "./mfiApi"
import { BaseXlsForm, SearchResponse, XlsForm, XLSFormSchemaByID } from "../../interfaces/dataInterfaces"
import { XlsFormResult } from "../../interfaces/xlsForm"
import { HeadersSheet, XlsFormData } from "../../interfaces/mappingInterfaces"
import { blobToDownload, blobToJsonView } from "../../utils/helpers"

export const xlsFormSchemas = mfiApi.injectEndpoints({
  endpoints: builder => ({
    getBaseXlsForm: builder.query<
      SearchResponse<BaseXlsForm>,
      { search: string; skip: number; take: number; isArchived?: boolean }
    >({
      query: ({ search, skip, take, isArchived = false }) => ({
        url: `XlsFormSchemas/GetBaseFormsBySearch`,
        params: { search, skip, take, isArchived },
      }),
      providesTags: ["BaseXlsFormMFI"],
    }),

    getAllBaseXlsForm: builder.query<BaseXlsForm[], void>({
      query: () => ({
        url: `XlsFormSchemas/GetBaseForms`,
      }),
      providesTags: ["BaseXlsFormMFI"],
    }),

    deleteXlsFormSchema: builder.mutation<void, number>({
      query: id => ({
        url: `XlsFormSchemas/Delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BaseXlsFormMFI", "XlsFormMFI", "BaseSchemasMFI"],
    }),

    saveXlsForm: builder.mutation<XlsFormResult, XlsFormData>({
      query: body => {
        const { name, baseXlsFormSchemaId, adm0code, file, questionLabelPosition, choiceLabelPosition } = body
        const bodyFormData = new FormData()
        bodyFormData.append("name", name)
        bodyFormData.append("baseXlsFormSchemaId", baseXlsFormSchemaId?.toString() as string)
        bodyFormData.append("adm0Code", adm0code.toString())
        bodyFormData.append("file", file)
        bodyFormData.append("questionLabelPosition", questionLabelPosition.toString())
        bodyFormData.append("choiceLabelPosition", choiceLabelPosition.toString())

        return {
          url: "XlsFormSchemas/SaveForm",
          method: "POST",
          body: bodyFormData,
          formData: true,
        }
      },
      invalidatesTags: ["BaseXlsFormMFI", "XlsFormMFI", "BaseSchemasMFI"],
    }),

    uploadXlsFormFile: builder.mutation<{ surveyHeaders: HeadersSheet[]; choicesHeaders: HeadersSheet[] }, FormData>({
      query: body => ({
        url: "XlsFormSchemas/FileHeaders",
        method: "POST",
        body,
      }),
    }),

    downloadXlsFormSchemasById: builder.mutation<void, { fileId: number; sourceName?: string; fileName?: string }>({
      query: ({ fileId, sourceName, fileName }) => ({
        url: `XlsFormSchemas/DownloadForm`,
        params: { id: fileId },
        method: "GET",
        responseHandler: async (response: Response) => {
          response.ok && blobToDownload(response, `${sourceName?.split(".xlsx_")[0] ?? fileName}.xlsx`)
        },
      }),
    }),

    getXlsForm: builder.query<
      SearchResponse<XlsForm>,
      { adm0Code: number; search: string; skip: number; take: number; isArchived?: boolean }
    >({
      query: ({ adm0Code, search, skip, take, isArchived = false }) => ({
        url: `XlsFormSchemas/Get`,
        params: { skip, take, adm0Code, search, isArchived },
      }),
      providesTags: ["XlsFormMFI"],
    }),

    getXlsFormSchemasById: builder.query<XLSFormSchemaByID, number>({
      query: id => ({
        url: `XlsFormSchemas/Get/${id}`,
      }),
      providesTags: ["XlsFormSchemaMFI"],
    }),

    downloadJsonSchemasById: builder.mutation<void, { id: number; module: "mfi" | "rpme" | "household" }>({
      query: ({ id, module }) => ({
        url: `/Admin/Srvc/Proxy?u=apiget/XlsForms/definition?xlsFormId=${id}&xlsFormType=${module}`,
        method: "GET",
        responseHandler: async (response: Response) => {
          if (!response.ok) {
            const res = await response.json()
            return res
          }
          blobToJsonView(response)
        },
      }),
    }),
  }),
})

export const {
  useGetAllBaseXlsFormQuery,
  useGetBaseXlsFormQuery,
  useDeleteXlsFormSchemaMutation,
  useSaveXlsFormMutation,
  useUploadXlsFormFileMutation,
  useGetXlsFormQuery,
  useGetXlsFormSchemasByIdQuery,
  useDownloadXlsFormSchemasByIdMutation,
  useDownloadJsonSchemasByIdMutation,
} = xlsFormSchemas
